import React, { type FC, type PropsWithChildren } from 'react';

import { getColor } from '@lucid/theme';
import { withImage } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';

import reactHTMLParser from '../../utils/reactHTMLParser';
import Box from '../box/box';
import Text from '../text/text';
import Heading from '../heading/heading';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Image from '../image/image';
import Link, { type LinkProps } from '../link/link';
import WistiaEmbed from '../wistia/wistia-embed';
import VisibleLottie from '../lottie/visible-lottie';
import VisibleVideo from '../video/visible-video/visible-video';
import type { Lucid } from '../../types/types';

export interface BackNForthProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** If the section has a dark background color */
  hasDarkBackground?: boolean;
  /** Sets the variant for section headings */
  headingVariant?:
    | Lucid.Theme.Component.Typography.Hero.VariantKeys
    | Lucid.Theme.Component.Typography.Heading.VariantKeys;
  /** Reverses the image and text for each component. */
  isReversed?: boolean;
  /** Passes the specific Link component props to the Link component */
  linkProps?: LinkProps;
  /** Determines whether the sections will be compact */
  isCompact?: boolean;
  /** The content for each section. */
  sections: Lucid.Directus.Generated.Back_N_Forth[];
  /* The color of the text */
  textColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The determines if the items are used in a section with a heading */
  hasSectionHeading?: boolean;
}

const BackNForth: FC<PropsWithChildren<BackNForthProps>> = ({
  className,
  hasDarkBackground = false,
  headingVariant = 'heading2XL',
  isReversed = false,
  textColor = 'coolGrey.100',
  linkProps,
  isCompact,
  sections,
  hasSectionHeading = false,
}) => {
  const bnfSections = sections.filter(
    section => section?.html || section?.image?.filename_disk || section.external_video_url,
  );

  return (
    <Box
      as="section"
      css={theme => ({
        label: 'backNForthSectionContainer',
        paddingBottom: theme.space.s16,
        [theme.breakpoints.bp768.min]: { paddingBottom: theme.space.s104 },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      {bnfSections.map((section, index) => {
        const { heading, html, image, background_image, logo, link, external_video_url } = section;
        const shouldUseAnimation = image?.type === 'application/json';
        const shouldUseImage = !shouldUseAnimation && image?.filename_disk && !external_video_url;
        let wistiaId: string = '';

        if (external_video_url && external_video_url.includes('wistia.com')) {
          const slashIndex = external_video_url.lastIndexOf('/');
          wistiaId = external_video_url.substring(slashIndex + 1);
        }
        const isOdd = isReversed ? index % 2 === 1 : index % 2 === 0;

        return (
          <Box
            key={`${kebabCase(heading)}-${index}`}
            as="section"
            css={theme => [
              {
                maxWidth: isCompact ? '810px' : 'initial',
                margin: isCompact ? 'auto' : 'initial',
                label: 'backNForth',
                display: 'grid',
                position: 'relative',
                gridTemplateColumns: '1fr',
                gridTemplateRows: '1fr auto',
                gridTemplateAreas: '"image" "content"',
                paddingTop: theme.space.s80,
                gap: theme.space.s24,
                [theme.breakpoints.bp900.min]: {
                  paddingTop: theme.space.s104,
                  gridTemplateRows: '1fr',
                },
              },
              isOdd && {
                [theme.breakpoints.bp768.min]: {
                  gridTemplateAreas: '"content image"',
                  gridTemplateColumns: isCompact ? '1fr 1fr' : '0.6fr 1fr',
                },
              },
              !isOdd && {
                [theme.breakpoints.bp768.min]: {
                  gridTemplateAreas: '"image content"',
                  gridTemplateColumns: isCompact ? '1fr 1fr' : '1fr 0.6fr',
                },
              },
            ]}
          >
            <Box
              css={{
                label: 'sectionImageContainer',
                gridArea: 'image',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxHeight: '540px',
              }}
            >
              {shouldUseAnimation && (
                <VisibleLottie
                  alt={image.translations?.[0]?.alt || ''}
                  isInitialInView={false}
                  path={withImage(image)}
                />
              )}
              {shouldUseImage &&
                (image?.filename_disk?.includes('.mp4') ? (
                  <VisibleVideo shouldLoop description={image?.translations?.[0]?.alt || ''} src={withImage(image)} />
                ) : (
                  <ImageWithPlaceholder
                    alt={image?.translations?.[0]?.alt || ''}
                    aspectRatio="4:3"
                    css={[
                      {
                        '& > .image': {
                          maxHeight: '540px',
                          maxWidth: '720px',
                        },
                      },
                      isCompact && {
                        '& > .image': {
                          maxHeight: '255px',
                          maxWidth: '340px',
                        },
                      },
                    ]}
                    src={withImage(image)}
                  />
                ))}
              {external_video_url && (
                <Box
                  css={{
                    width: '100%',
                    maxWidth: '720px',
                  }}
                >
                  {wistiaId ? <WistiaEmbed hashedId={wistiaId} /> : <VisibleVideo src={external_video_url} />}
                </Box>
              )}
              {background_image && (
                <Image
                  css={theme => [
                    {
                      maxWidth: '200vw',
                      maxHeight: '400px',
                      objectFit: 'cover',
                      position: 'absolute',
                      zIndex: theme.zIndices[0],
                      [theme.breakpoints.bp768.min]: {
                        maxWidth: '80%',
                      },
                    },
                    isOdd && {
                      [theme.breakpoints.bp768.min]: {
                        right: `-${theme.space.s16}px`,
                      },
                    },
                    !isOdd && {
                      [theme.breakpoints.bp768.min]: {
                        left: `-${theme.space.s16}px`,
                      },
                    },
                  ]}
                  imageAlt={background_image.translations?.[0]?.alt || ''}
                  imageSrc={withImage(background_image)}
                />
              )}
            </Box>
            <Box
              css={theme => [
                isOdd && {
                  justifySelf: 'end',
                },
                {
                  justifySelf: 'start',
                  label: 'sectionContent',
                  gridArea: 'content',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: `${theme.space.s32}px 0`,
                  maxWidth: '480px',
                  [theme.breakpoints.bp768.min]: {
                    maxWidth: 'initial',
                    alignSelf: 'center',
                  },
                },
              ]}
            >
              {logo?.filename_disk ? (
                <Image
                  css={theme => [
                    {
                      maxHeight: '80px',
                      maxWidth: '200px',
                      marginBottom: theme.space.s24,
                    },
                    isOdd && {
                      marginLeft: 'auto',
                    },
                    !isOdd && {
                      marginRight: 'auto',
                    },
                    logo.translations?.[0]?.alt?.toLowerCase()?.includes('zoom') && {
                      maxHeight: '40px',
                    },
                  ]}
                  imageAlt={logo.translations?.[0]?.alt || ''}
                  imageSrc={withImage(logo)}
                  loading="lazy"
                />
              ) : (
                <Heading
                  as={hasSectionHeading ? 'h3' : 'h2'}
                  css={theme => ({
                    marginBottom: theme.space.s24,
                    color: getColor({
                      color: textColor,
                      theme,
                    }),
                  })}
                  variant={headingVariant}
                >
                  {heading}
                </Heading>
              )}
              <Text
                as={html?.includes('<ul>') ? 'div' : 'p'}
                css={theme => [
                  {
                    color: getColor({
                      color: textColor,
                      theme,
                    }),
                  },
                  html?.includes('<ul>') && {
                    fontSize: theme.fontSizes.fs20,
                    fontWeight: theme.fontWeights.fw400,
                    lineHeight: theme.lineHeights.lh1_35,
                    li: {
                      listStylePosition: 'inside',
                    },
                  },
                ]}
                id={`bnf-item-text-${kebabCase(heading)}-${index}`}
                variant="paragraphM400"
              >
                {reactHTMLParser(html, {
                  replaceOptions: {
                    replaceTag: html?.includes('<ul>') ? undefined : 'span',
                  },
                })}
              </Text>
              {link && (
                <Link
                  {...linkProps}
                  aria-describedby={`bnf-item-text-${kebabCase(heading)}-${index}`}
                  aria-label={`${heading} - ${link.default_link_text}`}
                  href={link.final_url}
                  iconAnimation="navigational"
                  variant={hasDarkBackground ? 'primaryInverse' : 'primaryAlternate'}
                >
                  {link.default_link_text}
                </Link>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export { BackNForth };
export default BackNForth;
